import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Amenity categories
const amenityCategories = {
  'Information & Services': {
    icon: 'circle-info',
    items: [
      'information',
      'information - maps available',
      'information - ranger/staff member present',
      'information kiosk/bulletin board',
      'information - park newspaper available',
      'entrance passes for sale',
      'ticket sales',
      'permits issued',
      'junior ranger booklet available',
    ],
  },
  'Restrooms & Facilities': {
    icon: 'restroom',
    items: [
      'restroom',
      'restroom - family',
      'restroom - accessible',
      'toilet - flush',
      'toilet - vault/pit',
      'showers',
      'baby changing station',
      'trash/litter receptacles',
    ],
  },
  'Parking & Transportation': {
    icon: 'square-parking',
    items: [
      'parking - auto',
      'parking - bus/rv',
      'bus/shuttle stop',
      'fuel (unleaded)',
      'sewage dump station - boat/rv',
      'electric vehicle charging',
    ],
  },
  Accessibility: {
    icon: 'universal-access',
    items: [
      'wheelchair accessible',
      'accessible rooms',
      'accessible sites',
      'assistive listening systems',
      'captioned media',
      'braille',
      'sign language',
      'automated entrance',
      'elevator',
    ],
  },
  'Food & Shopping': {
    icon: 'utensils',
    items: [
      'food/drink - restaurant/table service',
      'food/drink - snacks',
      'food/drink - cafeteria',
      'food/drink - ice cream',
      'food/drink - vending',
      'gifts/souvenirs/books',
      'atm/cash machine',
    ],
  },
  'Water & Utilities': {
    icon: 'droplet',
    items: [
      'water - drinking/potable',
      'water - bottle-filling station',
      'internet/wifi available',
      'cellular signal',
      'telephone',
      'electricity hookup',
      'water hookup',
    ],
  },
  'Recreation & Education': {
    icon: 'camera',
    items: [
      'theater/auditorium',
      'historical/interpretive information/exhibits',
      'scenic view/photo spot',
      'picnic table',
      'benches/seating',
      'grill',
      'fire pit',
      'playground',
      'bicycle - rental',
    ],
  },
  'Medical & Safety': {
    icon: 'kit-medical',
    items: [
      'first aid kit available',
      'first aid/medical care available',
      'automated external defibrillator (aed)',
    ],
  },
  'Pet & Animal Services': {
    icon: 'paw',
    items: [
      'pets allowed',
      'animal-safe food storage',
      'hitching post/corral',
      'fish-cleaning station',
    ],
  },
  'Water Access': {
    icon: 'anchor',
    items: [
      'beach/water access',
      'dock/pier',
      'canoe/kayak/small boat launch',
      'boat launch',
      'marina',
    ],
  },
};

// Icon mapping for individual amenities
const iconMap = {
  restroom: 'restroom',
  'information - maps available': 'map',
  'theater/auditorium': 'masks-theater',
  'historical/interpretive information/exhibits': 'landmark',
  'gifts/souvenirs/books': 'gift',
  'cellular signal': 'signal',
  'captioned media': 'closed-captioning',
  'benches/seating': 'chair',
  information: 'circle-info',
  'information - ranger/staff member present': 'person',
  'information kiosk/bulletin board': 'thumbtack',
  'parking - auto': 'square-parking',
  'wheelchair accessible': 'wheelchair',
  'atm/cash machine': 'money-check-dollar',
  'accessible rooms': 'universal-access',
  'accessible sites': 'universal-access',
  'animal-safe food storage': 'vault',
  'assistive listening systems': 'ear-listen',
  trailhead: 'person-hiking',
  'pets allowed': 'paw',
  'trash/litter receptacles': 'trash',
  grill: 'fire-flame-simple',
  'permits issued': 'file-circle-check',
  'water - bottle-filling station': 'glass-water-droplet',
  'food/drink - ice cream': 'ice-cream',
  'internet/wifi available': 'wifi',
  'water - drinking/potable': 'droplet',
  'toilet - flush': 'toilet',
  'parking - bus/rv': 'caravan',
  'restroom - family': 'restroom',
  'junior ranger booklet available': 'book',
  'automated entrance': 'door-open',
  'beach/water access': 'umbrella-beach',
  'bus/shuttle stop': 'van-shuttle',
  'canoe/kayak/small boat launch': 'ship',
  'dock/pier': 'bridge-water',
  elevator: 'elevator',
  'entrance passes for sale': 'ticket-simple',
  'food/drink - restaurant/table service': 'utensils',
  'food/drink - snacks': 'cookie-bite',
  'hitching post/corral': 'hat-cowboy-side',
  'restroom - accessible': 'wheelchair',
  'scenic view/photo spot': 'panorama',
  showers: 'shower',
  'ticket sales': 'ticket',
  'baby changing station': 'baby',
  'bicycle - rental': 'person-biking',
  'fire pit': 'fire',
  'first aid kit available': 'kit-medical',
  'first aid/medical care available': 'bandage',
  'fish-cleaning station': 'fish',
  'food/drink - cafeteria': 'apple-whole',
  'fuel (unleaded)': 'gas-pump',
  'information - park newspaper available': 'newspaper',
  'sewage dump station - boat/rv': 'caravan',
  telephone: 'square-phone',
  'picnic table': 'table-picnic',
  playground: 'child',
};

function CategorySection({ title, icon, amenities = [], defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!amenities || amenities.length === 0) return null;

  return (
    <div className="w-full">
      <div className="mx-auto w-full rounded bg-white border-beige-100/40 border-2 mt-2 p-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full justify-between items-center rounded bg-beige-300 px-4 py-2 pt-3 text-left text-sm transition-all hover:bg-beige-100/30 focus:outline-none"
        >
          <div className="flex items-center gap-3">
            <i
              className={`fa fa-${icon} text-green-100 text-base w-5`}
              aria-hidden="true"
            />
            <span className="font-semibold text-brown-200">{title}</span>
            <span className="text-xs text-brown-100/70 bg-white/60 px-2 py-0.5 rounded-full">
              {amenities.length}
            </span>
          </div>
          <ChevronDownIcon
            className={clsx(
              'w-5 h-5 text-brown-100 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        <div
          className={clsx(
            'overflow-hidden transition-all duration-200',
            isOpen ? 'max-h-[500px]' : 'max-h-0'
          )}
        >
          <div className="px-4 pt-4 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
              {amenities.map((amenity, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-sm text-brown-200 py-1"
                >
                  <i
                    className={`fa fa-${iconMap[amenity.toLowerCase()] || 'circle'} text-brown-300 ${iconMap[amenity.toLowerCase()] ? 'text-xs' : 'text-[5px]'} w-4`}
                    aria-hidden="true"
                  />
                  <span>{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Amenities({ amenities }) {
  if (!amenities || amenities.length === 0) return null;

  // Group amenities by category
  const groupedAmenities = {};
  const uncategorized = [];

  amenities.forEach((amenity) => {
    const amenityName = amenity.name.toLowerCase();
    let found = false;

    for (const [category, config] of Object.entries(amenityCategories)) {
      if (
        config.items.some(
          (item) => amenityName.includes(item) || item.includes(amenityName)
        )
      ) {
        if (!groupedAmenities[category]) {
          groupedAmenities[category] = {
            icon: config.icon,
            items: [],
          };
        }
        groupedAmenities[category].items.push(amenity.name);
        found = true;
        break;
      }
    }

    if (!found) {
      uncategorized.push(amenity.name);
    }
  });

  // Sort categories by number of items (most items first)
  const sortedCategories = Object.entries(groupedAmenities).sort(
    (a, b) => b[1].items.length - a[1].items.length
  );

  return (
    <div>
      {sortedCategories.map(([category, config], index) => (
        <CategorySection
          key={category}
          title={category}
          icon={config.icon}
          amenities={config.items}
          defaultOpen={index === 0}
        />
      ))}

      {uncategorized.length > 0 && (
        <CategorySection
          title="Other Amenities"
          icon="ellipsis"
          amenities={uncategorized}
        />
      )}
    </div>
  );
}
